import type { SeverityLevel } from '@sentry/react';
import { addBreadcrumb, captureException, captureMessage, withScope } from '@sentry/react';

import { isError } from 'lodash-es';

import type { Level, TransportFn } from 'libs/logger';
import { isExceptionLogFnParams } from 'libs/logger';

type Primitive = number | string | boolean | bigint | symbol | null | undefined;

const LOG_LEVEL_TO_SEVERITY_MAP: Record<Level, SeverityLevel | undefined> = {
    fatal: 'fatal',
    error: 'error',
    warn: 'warning',
    info: 'info',
    debug: 'debug',
    trace: 'debug',
};

const getTagsFromParams = (paramsList: unknown[]): Record<string, Primitive> => {
    for (const param of paramsList) {
        if (param && typeof param === 'object') {
            const { tags } = param as { tags: unknown };

            if (tags && typeof tags === 'object') {
                return tags as Record<string, Primitive>;
            }
        }
    }

    return {};
};

const getCauseFromParams = (paramsList: unknown[]): Record<string, unknown> | null => {
    for (const param of paramsList) {
        if (param && typeof param === 'object') {
            const { cause } = param as { cause: unknown };

            if (cause && typeof cause === 'object') {
                return cause as Record<string, unknown>;
            }
        }
    }

    return null;
};

export const transport: TransportFn = (params) => {
    const { level, levelsConfig, methodFnArgs, isLevelEnabled } = params;
    const { value: levelValue } = levelsConfig[level];

    const tags = {
        ...getTagsFromParams(methodFnArgs),
    };

    const cause = getCauseFromParams(methodFnArgs);

    // capture all logs above 'warn' level as exceptions by sentry
    if (levelValue > levelsConfig.warn.value && isExceptionLogFnParams(params)) {
        const { error } = params;

        withScope((scope) => {
            try {
                for (const tagName of Object.keys(tags)) {
                    scope.setTag(tagName, tags[tagName]);
                }

                if (cause) {
                    scope.setContext('cause', cause);
                }

                captureException(isError(error) ? error : new Error(methodFnArgs.join(', ')));
            } catch (err) {
                captureException(err);
            }
        });

        return;
    }

    if (levelValue === levelsConfig.info.value) {
        withScope((scope) => {
            try {
                for (const tagName of Object.keys(tags)) {
                    scope.setTag(tagName, tags[tagName]);
                }

                if (cause) {
                    scope.setContext('cause', cause);
                }

                captureMessage(methodFnArgs[0] as string, 'info');
            } catch (err) {
                captureException(err);
            }
        });

        return;
    }

    // add logs for disabled levels and below to sentry breadcrumbs
    if (!isLevelEnabled) {
        addBreadcrumb({
            type: 'debug',
            category: 'console',
            message: methodFnArgs.join(', '),
            level: LOG_LEVEL_TO_SEVERITY_MAP[level],
            data: { arguments: methodFnArgs, logger: 'console' },
        });
        return;
    }
};
